import $ from 'jquery';
import * as React from 'react';
import Textarea from 'react-textarea-autosize';

import { getMobileDevicePlatform, PLATFORMS } from 'helper/device';
import unfocusUnselectField from 'helper/unfocusUnselectField';
import { delayed } from 'lib/Job';

import Icon from 'components/atoms/Icon';

export type EditableAreaRenderButtonBarOptionsType = {
	onClickClose: () => void;
};
export type EditableAreaPropsType = {
	text: string;
	title?: string;
	placeholder?: string;
	isEditing: boolean;
	isSlogan?: boolean;
	renderButtonBar?: (options: EditableAreaRenderButtonBarOptionsType) => any;
	onChange?: (value: string) => void;
	onCloseFactory?: (handleClose: () => void) => void;
	onChangeEditStatus: () => void;
	onFocus?: (textareaDomElement: HTMLTextAreaElement) => void;
	onBlur?: (textareaDomElement: HTMLTextAreaElement) => void;
};

export type EditableAreaStateType = {
	value: string;
};

const selectAll = (input: any) => {
	requestAnimationFrame(() => {
		input.setSelectionRange(0, Number.MAX_SAFE_INTEGER || 9999);
		input.selectionStart = 0;
		input.selectionEnd = 9999;
	});
};

export default class EditableArea extends React.Component<EditableAreaPropsType, EditableAreaStateType> {
	public static displayName = 'EditableArea';
	public static defaultValues = {
		isSlogan: false,
	};

	public textareaDomElement: HTMLTextAreaElement | undefined;

	constructor(props: EditableAreaPropsType) {
		super(props);
		this.state = {
			value: props.text || '',
		};
		if (props.onCloseFactory) {
			props.onCloseFactory(this.handleClose);
		}
	}

	get isAndroid(): boolean {
		return getMobileDevicePlatform() === PLATFORMS.ANDROID;
	}

	public componentDidUpdate(prevProps: EditableAreaPropsType) {
		if (!prevProps.isEditing && this.props.isEditing) {
			this.focusTextarea();
			this.selectTextarea();
		}

		if (prevProps.isEditing && !this.props.isEditing) {
			this.unfocusTextarea();
		}
	}

	public selectTextarea = () => {
		if (!this.textareaDomElement) {
			return;
		}
		const DomNode: HTMLTextAreaElement = this.textareaDomElement;
		selectAll(DomNode);
	};

	public focusTextarea = () => {
		if (!this.textareaDomElement) {
			return;
		}

		$(this.textareaDomElement).focus();
	};

	/*
	 * @see jira.migros.net/browse/PULSWM-2801
	 */
	public androidScrollFix = () => {
		if (!this.isAndroid || !this.textareaDomElement) {
			return;
		}
		const $textarea = $(this.textareaDomElement);
		const $card = $textarea.closest('.m-card');

		window.scrollTo(0, 0);
		(document.body || {}).scrollTop = 0;

		(window as any).$card = $card as JQuery;

		delayed(() => {
			$card.find('.m-card__card').animate(
				{
					scrollTop:
						$card.find('.m-card__head')!.height()! +
						$card
							.find('.m-card__detail')!
							.find('.m-rich')!
							.height()! +
						$card.find('.m-card__info')!.height()! +
						60,
				},
				0
			);
		}, 500);
	};

	public unfocusTextarea = () => {
		if (!this.textareaDomElement) {
			return;
		}
		unfocusUnselectField();
	};

	public handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		this.setState({
			value: event.target.value,
		});
		if (this.props.onChange) {
			this.props.onChange(event.target.value);
		}
	};

	public handleBlur = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		if (this.props.onBlur) {
			this.props.onBlur(event.target);
		}
	};

	public handleChangeEditStatus = () => {
		this.props.onChangeEditStatus();
	};

	public handleFocus = () => {
		if (this.textareaDomElement && this.props.onFocus) {
			this.props.onFocus(this.textareaDomElement);
		}

		/*
		 * @see jira.migros.net/browse/PULSWM-2801
		 */
		this.androidScrollFix();
	};

	public handleClose = () => {
		this.setState({
			value: this.props.text,
		});
	};

	public renderLabel() {
		const { title } = this.props;
		return title ? <div className="m-editable-area__label">{title}</div> : null;
	}

	public renderButtonBar() {
		const { renderButtonBar } = this.props;
		return renderButtonBar ? (
			<div className="m-editable-area__button-bar">
				{renderButtonBar({
					onClickClose: this.handleClose,
				})}
			</div>
		) : null;
	}

	public renderQuote(hAlign = 'top') {
		return (
			<span className={`m-editable-area__quote m-editable-area__quote--${hAlign}`}>
				<Icon iconName="quote-filled" />
			</span>
		);
	}

	public renderTextarea() {
		const { value } = this.state;
		const { isEditing, placeholder } = this.props;

		return (
			<Textarea
				inputRef={(input: HTMLTextAreaElement) => (this.textareaDomElement = input)}
				name="m-editable-area__textarea"
				placeholder={placeholder}
				className="m-editable-area__textarea"
				spellCheck={false}
				autoComplete="off"
				autoCapitalize="none"
				readOnly={!isEditing}
				disabled={!isEditing}
				value={value}
				onChange={this.handleChange}
				onBlur={this.handleBlur}
				onFocus={this.handleFocus}
			/>
		);
	}

	public renderTriangle() {
		return (
			<a className="m-editable-area__triangle">
				<span className="m-editable-area__triangle-icon" onClick={this.handleChangeEditStatus}>
					<Icon iconName="pen" />
				</span>
			</a>
		);
	}

	public render() {
		const { isEditing, isSlogan } = this.props;
		return (
			<div
				className={`m-editable-area${isEditing ? ' state-m-editable-area--editable' : ''}${
					isSlogan ? ' m-editable-area--slogan' : ''
				}`}
			>
				<div className="m-editable-area__wrap">
					{this.renderLabel()}
					{this.renderTextarea()}
					{this.renderQuote()}
					{this.renderQuote('bottom')}
				</div>
				{this.renderTriangle()}
				{this.renderButtonBar()}
			</div>
		);
	}
}
